import { type AnalysisType } from "@/generated/graphql"
import { type AnalysisWithCompsType } from "@/queries/analysis"

import {
  createCardClickedEvent,
  createComponentClickedEvent,
  createComponentViewedEvent,
  createGenericActionEvent,
  createListScrolledEvent,
  createScreenViewedEvent,
  createSubmittedEvent,
  track,
} from "./base"
import type { AppScreen } from "@/constants/Screens"
import { getAnalysisType, getPropertyStatus } from "@/utils/AnalysisUtils"
import { type CompSortOption, type CompSortOrder } from "@/types"
import {
  currentSortingState,
  isCurrentSortReversed,
} from "@/stores/compSortingDataStore"
import ToolTips from "@/constants/ToolTips"

export const analyzeViewed = () => {
  track(
    createScreenViewedEvent({
      current_screen: "Analyze",
    })
  )
}

export const analyzePropertySearchViewed = () => {
  track(
    createScreenViewedEvent({
      current_screen: "Analyze Property Search",
    })
  )
}

export const compSearchViewed = () => {
  track(
    createScreenViewedEvent({
      current_screen: "Search Comps",
    })
  )
}

export const analysisLoadingViewed = () => {
  track(
    createScreenViewedEvent({
      current_screen: "Analysis Loading",
    })
  )
}

export const propertyNotFoundViewed = ({
  propertyAddress,
}: {
  propertyAddress: string
}) => {
  track(
    createScreenViewedEvent({
      current_screen: "Property Not Found",
      property_address: propertyAddress,
    })
  )
}

export const analysisSubmitted = ({
  propertyAddress,
}: {
  propertyAddress: string
}) => {
  track(
    createSubmittedEvent({
      object_name: "Analysis",
      current_screen: "Analyze Property Search",
      to_screen: "Subject Property",
      component_type: "list_option",
      component_text: propertyAddress,
      submitted_values: {
        property_address: propertyAddress,
      },
    })
  )
}

export const searchedCompSubmitted = ({
  propertyAddress,
}: {
  propertyAddress: string
}) => {
  track(
    createSubmittedEvent({
      object_name: "Comp Search",
      current_screen: "Search Comps",
      to_screen: "Subject Property",
      component_type: "list_option",
      component_text: propertyAddress,
      submitted_values: {
        property_address: propertyAddress,
      },
    })
  )
}

export const analyzePropertySearchClicked = () => {
  track(
    createComponentClickedEvent({
      current_screen: "Analyze",
      to_screen: "Analyze Property Search",
      component_function: "analyze property search",
      component_type: "input_field",
      component_text: "Enter An Address",
    })
  )
}

export const analyzePropertyARVFeedbackClicked = (
  feedback: string,
  analysis: AnalysisType
) => {
  console.log(
    createComponentClickedEvent({
      current_screen: "Analyze",
      component_function: "analyze property search",
      component_type: "button",
      component_text: feedback,
      clicked_values: {
        analysis_id: analysis.id,
        sp_address: analysis.property.fullAddress,
        arv_feedback: feedback,
        arv: analysis.afterRepairValue,
        purchase_price: analysis.purchasePrice,
      },
    })
  )
  track(
    createComponentClickedEvent({
      current_screen: "Analyze",
      component_function: "analyze property search",
      component_type: "button",
      component_text: feedback,
      clicked_values: {
        analysis_id: analysis.id,
        sp_address: analysis.property.fullAddress,
        arv_feedback: feedback,
        arv: analysis.afterRepairValue,
        purchase_price: analysis.purchasePrice,
      },
    })
  )
}

export const analyzePropertyGiveARVFeedbackClicked = () => {
  track(
    createComponentClickedEvent({
      current_screen: "Analyze",
      component_function: "open arv feedback popup",
      component_type: "button",
      component_text: "Give Feedback",
    })
  )
}

export const analyzePropertyARVFeedbackViewed = () => {
  track(
    createComponentViewedEvent({
      current_screen: "Analyze",
      component_function: "ARV Feedback Popup",
      component_type: "banner",
      component_text: "How does the ARV estimate compare to your expectations?",
    })
  )
}

export const subjectPropertyViewed = ({
  analysis,
  activeTab,
  calculatorStrategy,
  fromScreen = "Unknown",
}: {
  analysis: AnalysisType
  activeTab: string
  calculatorStrategy: string | null
  fromScreen?: AppScreen
}) => {
  const analysisSegmentProps = {
    analysis_id: analysis.id,
    property_address: analysis.property.fullAddress,
    bed: analysis.bedrooms,
    bath: analysis.bathrooms,
    sq_ft: analysis.area,
    purchase_price: analysis.purchasePrice,
    arv: analysis.afterRepairValue,
    profit: analysis.profitPotential,
    num_comps: analysis.comps?.length || 0,
    analysis_type: getAnalysisType(analysis),
  }

  const isCompsTab = activeTab === "Comps"

  track(
    createScreenViewedEvent({
      current_screen: "Subject Property",
      current_tab: activeTab.toLowerCase(),
      from_screen: fromScreen,
      viewed_values: {
        ...analysisSegmentProps,
        mile_radius: isCompsTab ? analysis.distance : undefined,
        calculator_type: !isCompsTab ? calculatorStrategy : undefined,
        property_status: getPropertyStatus(analysis),
      },
    })
  )
}

export const editSubjectPropertyViewed = ({
  analysis,
}: {
  analysis: AnalysisType
}) => {
  const analysisSegmentProps = {
    analysis_id: analysis.id,
    property_address: analysis.property.fullAddress,
    bed: analysis.bedrooms,
    bath: analysis.bathrooms,
    sq_ft: analysis.area,
    purchase_price: analysis.purchasePrice,
  }

  track(
    createScreenViewedEvent({
      current_screen: "Edit Subject Property",
      from_screen: "Subject Property",
      viewed_values: {
        ...analysisSegmentProps,
        property_status: getPropertyStatus(analysis),
      },
    })
  )
}

// Compact Header aka "Full Screen Comp List"
export const compactCompListViewed = ({
  analysis,
  fromScreen = "Unknown",
}: {
  analysis: Pick<AnalysisType, "id" | "property" | "isSourced" | "sharedFrom">
  fromScreen?: AppScreen
}) => {
  const analysisSegmentProps = {
    analysis_id: analysis.id,
    property_address: analysis.property.fullAddress,
    analysis_type: getAnalysisType(analysis),
  }

  track(
    createScreenViewedEvent({
      current_screen: "Full Screen Comp List",
      from_screen: fromScreen,
      viewed_values: {
        ...analysisSegmentProps,
      },
    })
  )
}

// Compact Header displaying aka "Full Screen Comp List"
export const fullScreenCompListScrolled = ({
  scrolledTo,
}: {
  scrolledTo: number
}) => {
  track(
    createListScrolledEvent({
      current_screen: "Subject Property Comps",
      list_type: "comp",
      scrolled_to: scrolledTo,
    })
  )
}

export const compDetailViewed = ({
  analysis,
  comp,
  fromScreen = "Unknown",
}: {
  analysis: Pick<AnalysisType, "id" | "property" | "isSourced" | "sharedFrom">
  comp: { address: string; id: string }
  fromScreen?: AppScreen
}) => {
  const analysisSegmentProps = {
    analysis_id: analysis.id,
    subject_property_address: analysis.property.fullAddress,
    analysis_type: getAnalysisType(analysis),
  }

  track(
    createScreenViewedEvent({
      current_screen: "Comp Detail",
      from_screen: fromScreen,
      viewed_values: {
        ...analysisSegmentProps,
        comp_id: comp.id,
        comp_address: comp.address,
      },
    })
  )
}

export const compMapViewed = ({
  analysis,
  fromScreen = "Unknown",
}: {
  analysis: Pick<AnalysisType, "id" | "property" | "isSourced" | "sharedFrom">
  fromScreen?: AppScreen
}) => {
  const analysisSegmentProps = {
    analysis_id: analysis.id,
    property_address: analysis.property.fullAddress,
    analysis_type: getAnalysisType(analysis),
  }

  track(
    createScreenViewedEvent({
      current_screen: "Comp Map",
      from_screen: fromScreen,
      viewed_values: {
        ...analysisSegmentProps,
      },
    })
  )
}

export const compMapPlaceholderViewed = ({
  analysis,
  fromScreen = "Unknown",
}: {
  analysis: Pick<AnalysisType, "id" | "property" | "isSourced" | "sharedFrom">
  fromScreen?: AppScreen
}) => {
  const analysisSegmentProps = {
    analysis_id: analysis.id,
    property_address: analysis.property.fullAddress,
    analysis_type: getAnalysisType(analysis),
  }

  track(
    createScreenViewedEvent({
      current_screen: "Comp Map Placeholder",
      from_screen: fromScreen,
      viewed_values: {
        ...analysisSegmentProps,
      },
    })
  )
}

export const compMapPlaceholderAppInstallClicked = ({
  analysis,
  badgeType,
}: {
  analysis: Pick<AnalysisType, "id" | "property" | "isSourced" | "sharedFrom">
  badgeType: "apple_install_badge" | "google_install_badge"
}) => {
  const analysisSegmentProps = {
    analysis_id: analysis.id,
    property_address: analysis.property.fullAddress,
    analysis_type: getAnalysisType(analysis),
  }

  track(
    createComponentClickedEvent({
      current_screen: "Comp Map Placeholder",
      component_type: badgeType,
      component_function: "link to app store",
      clicked_values: {
        ...analysisSegmentProps,
      },
    })
  )
}
export const customizeCompsPlaceholderAppInstallClicked = ({
  analysis,
  badgeType,
}: {
  analysis: Pick<AnalysisType, "id" | "property" | "isSourced" | "sharedFrom">
  badgeType: "apple_install_badge" | "google_install_badge"
}) => {
  const analysisSegmentProps = {
    analysis_id: analysis.id,
    property_address: analysis.property.fullAddress,
    analysis_type: getAnalysisType(analysis),
  }

  track(
    createComponentClickedEvent({
      current_screen: "Customize Comps Placeholder",
      component_type: badgeType,
      component_function: "link to app store",
      clicked_values: {
        ...analysisSegmentProps,
      },
    })
  )
}

export const loanButtonClicked = ({
  currentScreen,
}: {
  currentScreen: AppScreen
}) => {
  track(
    createComponentClickedEvent({
      current_screen: currentScreen,
      component_type: "button",
      component_function: "start loan app",
      component_text: "get a loan",
    })
  )
}

export const compSortClick = ({
  label,
  sortOption,
}: {
  label: string
  sortOption: CompSortOption
}) => {
  const current = currentSortingState.get()
  const sortOrdering: CompSortOrder =
    current.currentCriterion !== sortOption.criterion ||
    isCurrentSortReversed.get()
      ? "default"
      : "reverse"

  track(
    createComponentClickedEvent({
      current_screen: "Subject Property",
      component_type: "pill",
      component_function: "comp sort",
      component_text: label,
      clicked_values: {
        sort_type: sortOption.criterion || "unknown",
        sort_ordering: sortOrdering,
      },
    })
  )
}

export const mapCompClicked = ({
  comp,
}: {
  comp: { address: string; id: string }
}) => {
  track(
    createComponentClickedEvent({
      current_screen: "Comp Map",
      component_type: "map_place_icon",
      component_function: "view comp details",
      clicked_values: {
        comp_id: comp.id,
        comp_address: comp.address,
      },
    })
  )
}

export const compCardClicked = ({
  cardIndex,
  analysis,
  comp,
}: {
  cardIndex: number
  analysis: AnalysisType
  comp: { id: string; address: string }
}) => {
  track(
    createCardClickedEvent({
      current_screen: "Subject Property",
      to_screen: "Comp Detail",
      card_type: "comp list",
      card_list_number: cardIndex,
      card_values: {
        analysis_id: analysis.id,
        subject_property_id: analysis.property.id,
        subject_property_address: analysis.property.fullAddress,
        comp_id: comp.id,
        comp_property_address: comp.address,
      },
    })
  )
}

export const compDetailsListScrolled = ({
  scrolledTo,
}: {
  scrolledTo: number
}) => {
  track(
    createListScrolledEvent({
      current_screen: "Comp Detail",
      list_type: "comp",
      scrolled_to: scrolledTo,
    })
  )
}

export const compDetailCardClicked = ({
  cardIndex,
  analysis,
  comp,
}: {
  cardIndex: number
  analysis: AnalysisWithCompsType
  comp: { id: string; address: string }
}) => {
  track(
    createCardClickedEvent({
      current_screen: "Comp Detail",
      to_screen: "Comp Detail",
      card_type: "comp photo",
      card_list_number: cardIndex,
      card_values: {
        analysis_id: analysis.id,
        subject_property_id: analysis.property.id,
        subject_property_address: analysis.property.fullAddress,
        comp_id: comp.id,
        comp_property_address: comp.address,
      },
    })
  )
}

export const compCarouselCardClicked = ({
  cardIndex,
  analysis,
  comp,
}: {
  cardIndex: number
  analysis: AnalysisWithCompsType
  comp: { id: string; address: string }
}) => {
  track(
    createCardClickedEvent({
      current_screen: "Comp Detail",
      to_screen: "Comp Detail",
      card_type: "comp carousel photo",
      card_list_number: cardIndex,
      card_values: {
        analysis_id: analysis.id,
        subject_property_id: analysis.property.id,
        subject_property_address: analysis.property.fullAddress,
        comp_id: comp.id,
        comp_property_address: comp.address,
      },
    })
  )
}

export const compAdded = ({
  currentScreen,
  analysis,
  comp,
}: {
  currentScreen: AppScreen
  analysis: AnalysisWithCompsType
  comp: { id: string; address: string }
}) => {
  track(
    createGenericActionEvent({
      event_name: "Comp Added",
      current_screen: currentScreen,
      component_type: "icon",
      values: {
        analysis_id: analysis.id,
        subject_property_id: analysis.property.id,
        subject_property_address: analysis.property.fullAddress,
        comp_id: comp.id,
        comp_property_address: comp.address,
        property_status: getPropertyStatus(analysis),
      },
    })
  )
}

export const compRemoved = ({
  currentScreen,
  analysis,
  comp,
}: {
  currentScreen: AppScreen
  analysis: AnalysisWithCompsType
  comp: { id: string; address: string }
}) => {
  track(
    createGenericActionEvent({
      event_name: "Comp Removed",
      current_screen: currentScreen,
      component_type: "icon",
      values: {
        analysis_id: analysis.id,
        subject_property_id: analysis.property.id,
        subject_property_address: analysis.property.fullAddress,
        comp_id: comp.id,
        comp_property_address: comp.address,
        property_status: getPropertyStatus(analysis),
      },
    })
  )
}

export const analysisEditSubmitted = ({
  analysis,
  editedField,
  fromScreen,
  toScreen,
}: {
  analysis: AnalysisType
  editedField: {
    fieldName: string
    newValue: any
  }
  fromScreen: AppScreen
  toScreen?: AppScreen
}) => {
  const eventProperties = {
    object_name: "Analysis Edit",
    current_screen: fromScreen,
    to_screen: toScreen ?? "Unknown",
    component_type: "input_field",
    component_text: editedField.fieldName,
    submitted_values: {
      analysis_id: analysis.id,
      subject_property_address: analysis.property.fullAddress,
      subject_property_id: analysis.property.id,
      edited_field_name: editedField.fieldName,
      edited_field_value: editedField.newValue,
      property_status: getPropertyStatus(analysis),
    },
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
  track(createSubmittedEvent(eventProperties as any))
}

export const shareClicked = (analysis: AnalysisType) => {
  track(
    createComponentClickedEvent({
      current_screen: "Subject Property",
      component_type: "button",
      component_function: "share an analysis",
      action_taken: "share sheet presented to user",
      clicked_values: {
        analysis_id: analysis.id,
        subject_property_address: analysis.property.fullAddress,
        subject_property_id: analysis.property.id,
        property_status: getPropertyStatus(analysis),
      },
    })
  )
}

export const sortCompsButtonClicked = ({
  currentScreen,
  toScreen,
}: {
  currentScreen: AppScreen
  toScreen: AppScreen
}) => {
  track(
    createComponentClickedEvent({
      current_screen: currentScreen,
      to_screen: toScreen,
      component_type: "button",
      component_function: "pop up sort comps menu",
      component_text: "Sort",
    })
  )
}

export const findCompsButtonClicked = ({
  currentScreen,
  toScreen,
}: {
  currentScreen: AppScreen
  toScreen: AppScreen
}) => {
  track(
    createComponentClickedEvent({
      current_screen: currentScreen,
      to_screen: toScreen,
      component_type: "button",
      component_function: "go to search comps screen",
      component_text: "Find A Comp",
    })
  )
}

export const customizeCompsButtonClicked = ({
  currentScreen,
  toScreen,
}: {
  currentScreen: AppScreen
  toScreen: AppScreen
}) => {
  track(
    createComponentClickedEvent({
      current_screen: currentScreen,
      to_screen: toScreen,
      component_type: "button",
      component_function: "go to customize comps menu",
      component_text: "Customize Comps",
    })
  )
}

export const customizeCompsWalkthroughViewed = () => {
  track(
    createComponentClickedEvent({
      current_screen: "Subject Property",
      component_type: "button",
      component_function: "displays teaser when user first visits screen",
      component_text: ToolTips.copilotCustomComps,
    })
  )
}

export const customizeCompsWalkthroughClosed = () => {
  track(
    createComponentClickedEvent({
      current_screen: "Subject Property",
      component_type: "button",
      component_function: "customize comps teaser was closed",
      component_text: ToolTips.copilotCustomComps,
    })
  )
}

// Comp Selection

export const compSelectionWalkthroughStep = (
  stepNumber: number,
  componentText: string,
  stepDescription: string
) => {
  track(
    createComponentClickedEvent({
      current_screen: "Subject Property",
      component_function: "navigate comp selection walkthrough step",
      component_type: "button",
      component_text: componentText,
      clicked_values: {
        step_number: stepNumber,
        step_description: stepDescription,
      },
    })
  )
}

export const compSelectionWalkthroughSkipped = () => {
  track(
    createComponentClickedEvent({
      current_screen: "Subject Property",
      component_type: "button",
      component_function: "comp selection walkthrough skipped",
      component_text: "Skip",
    })
  )
}

// Tracking clicks on low comps tooltip icon
export const lowCompsTooltipClicked = (analysis: AnalysisType) => {
  track(
    createComponentClickedEvent({
      current_screen: "Subject Property",
      component_function: "view tooltip",
      component_type: "button",
      component_text: "low comps",
      clicked_values: {
        tooltip_name: "low comps",
        icon_type: "info",
        analysis_id: analysis.id,
        subject_property_id: analysis.property.id,
        subject_property_address: analysis.property.fullAddress,
        property_status: getPropertyStatus(analysis),
      },
    })
  )
}

export const compSelectionWalkthroughStarted = (
  analysis?: AnalysisType | undefined | null
) => {
  track(
    createGenericActionEvent({
      component_type: "walkthrough",
      event_name: "Comp Selection Walkthrough Started",
      current_screen: "Subject Property",
      values: {
        completion_status: "Started",
        analysis_id: analysis?.id,
        subject_property_id: analysis?.property.id,
        subject_property_address: analysis?.property.fullAddress,
      },
    })
  )
}

export const compSelectionWalkthroughCompleted = (analysisId: string) => {
  track(
    createGenericActionEvent({
      component_type: "walkthrough",
      event_name: "Comp Selection Walkthrough Completed",
      current_screen: "Subject Property",
      values: {
        completion_status: "Completed",
        steps_completed: "All Steps",
        analysis_id: analysisId,
      },
    })
  )
}

export const compSelectionAutoSelectClicked = (
  analysis?: AnalysisType | undefined | null
) => {
  track(
    createComponentClickedEvent({
      current_screen: "Subject Property",
      component_type: "button",
      component_function: "activate automatically selected comps",
      component_text: "Select for me",
      clicked_values: {
        analysis_id: analysis?.id,
        subject_property_id: analysis?.property.id,
        subject_property_address: analysis?.property.fullAddress,
      },
    })
  )
}
